import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import all translation files
import th from '../locales/th.json';
import en from '../locales/en.json';
import de from '../locales/de.json';
import fr from '../locales/fr.json';
import ru from '../locales/ru.json';
import zhCN from '../locales/zh-CN.json';
import zhTW from '../locales/zh-TW.json';
import ko from '../locales/ko.json';
import ja from '../locales/ja.json';
import ar from '../locales/ar.json';
import { localeConfig } from './locales';

// Normalize language code (e.g., 'en-US' -> 'en', 'th-TH' -> 'th')
const normalizeLanguageCode = (lang) => {
  if (!lang) return 'th';
  
  // Handle special cases first
  if (lang.startsWith('zh-CN') || lang === 'zh-CN') return 'zh-CN';
  if (lang.startsWith('zh-TW') || lang === 'zh-TW') return 'zh-TW';
  
  // Extract base language (e.g., 'en-US' -> 'en', 'th-TH' -> 'th')
  const baseLang = lang.split('-')[0];
  
  // Map to supported languages
  const supportedLangs = ['th', 'en', 'de', 'fr', 'ru', 'zh-CN', 'zh-TW', 'ko', 'ja', 'ar'];
  if (supportedLangs.includes(baseLang)) {
    return baseLang;
  }
  
  // Default to 'th' if not supported
  return 'th';
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      th: { translation: th },
      en: { translation: en },
      de: { translation: de },
      fr: { translation: fr },
      ru: { translation: ru },
      'zh-CN': { translation: zhCN },
      'zh-TW': { translation: zhTW },
      ko: { translation: ko },
      ja: { translation: ja },
      ar: { translation: ar },
    },
    lng: 'th', // Set default language to Thai
    fallbackLng: 'th',
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      // Only check localStorage, don't use navigator to avoid defaulting to browser language
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
      // Normalize detected language
      convertDetectedLanguage: (lng) => {
        // If no language detected or empty, default to Thai
        if (!lng || lng.trim() === '') {
          return 'th';
        }
        return normalizeLanguageCode(lng);
      },
    },
    react: {
      useSuspense: false,
    },
  });

// Normalize language when it changes
i18n.on('languageChanged', (lng) => {
  const normalizedLang = normalizeLanguageCode(lng);
  if (normalizedLang !== lng) {
    i18n.changeLanguage(normalizedLang);
    return;
  }
  
  const locale = localeConfig[normalizedLang];
  if (locale) {
    document.documentElement.setAttribute('dir', locale.rtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', normalizedLang);
  } else {
    document.documentElement.setAttribute('dir', 'ltr');
    document.documentElement.setAttribute('lang', normalizedLang);
  }
});

// Force Thai as default if no language is set in localStorage
const savedLang = localStorage.getItem('i18nextLng');
if (!savedLang) {
  // No saved language, force Thai
  i18n.changeLanguage('th');
}

// Set initial direction
const currentLang = normalizeLanguageCode(i18n.language || 'th');
const locale = localeConfig[currentLang];
if (locale) {
  document.documentElement.setAttribute('dir', locale.rtl ? 'rtl' : 'ltr');
  document.documentElement.setAttribute('lang', currentLang);
} else {
  document.documentElement.setAttribute('dir', 'ltr');
  document.documentElement.setAttribute('lang', currentLang);
}

export default i18n;

